import { useEffect, useState } from "react";
import { AssessmentShell } from "../components/AssessmentShell";
import { useCustomSpeechToText } from "../hooks/useCustomSpeechToText";
import { useNavigate } from "react-router";
import { LTitle } from "../../../common/components/LTitle";
import { useApi } from "../../../services/useApi";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { Word, WordResponseType } from "../../../common/store/assessmentSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { Colors } from "../../../styles/colors";
import { useCountDown } from "../../../common/helpers/countHelper";
import { ResultType } from "../../../utils/stt/useSpeechToText";
import { updateCurrentFlow } from "../../../common/store/assessmentFlowSlice";
import { NavItems } from "../../../common/helpers/navigation";
import { translate } from "react-i18nify";
import { intersection } from "lodash";
import { LCountDown } from "@components/LCountDown";
import { LProgressCircle } from "@components/LProgressCircle";
import { sanitizeResponse } from "@helpers/stringHelper";
import { playAudioAsync } from "@fassessment/hooks/useAudio";
import { AssessmentTimeout } from "@fassessment/components/AssessmentTimeout";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { v4 as uuidv4 } from "uuid";
import { SttSourceType } from "@store/generalTypes";

const getNextElement = (element: Word, wordList: Word[]): Word | undefined => wordList[wordList.indexOf(element) + 1];

const getAcceptedWords = (word: Word): string[] => [word.value, ...word.alternates];

export const AssessmentPresentTargets = (): JSX.Element => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const appInsights = useAppInsightsContext();

    const assessment = useAppSelector((x) => x.assessment);
    const wordTime = useAppSelector((x) => x.globalSettings.presentTargets);

    const showTimer = useAppSelector((x) => x.globalSettings.showTimers);

    const [assessmentFlow] = useState(useAppSelector((x) => x.assessmentFlow));

    const currentFlow = assessmentFlow.flows[assessmentFlow.currentFlow];

    const { uploadAssessmentResponse } = useApi();

    const loadAudioForWord = (word: Word): void => {
        const newAudio = new Audio(word.audioInstructionUrl);
        newAudio.load();
        newAudio.loop = false;
        newAudio.volume = 0.6;

        setCurrentAudio(newAudio);
    };

    const [currentWord, setCurrentWord] = useState(assessment.wordLists[currentFlow.list].words[0]);
    const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement>();
    const [acceptedWords, setAcceptedWords] = useState(getAcceptedWords(currentWord));
    const [finished, setFinished] = useState(false);
    const [promptTitle, setPromptTitle] = useState<string>();

    const [isSecondTry, setIsSecondTry] = useState(false);
    const [hasSucceeded, setHasSucceded] = useState(false);
    const [resultWord, setResultWord] = useState<ResultType>();
    const [alternates, setAlternates] = useState<Word[]>([]);

    const [showCountdown, setShowCountDown] = useState(true);

    const logData = {
        id: uuidv4(),
        assessmentId: `present-target-${assessment.id}`,
        flow: "",
        word: "",
        part: 0,
        context: "",
        text: ""
    };

    const handleToggleCountdown = async (): Promise<void> => {
        logPresentTargetFlow("start handleToggleCountdown", { currentWordValue: currentWord.value });

        setShowCountDown(false);
        logPresentTargetFlow("trace handleToggleCountdown startSpeechToText");

        await startSpeechToText(currentWord.value);

        logPresentTargetFlow("trace handleToggleCountdown startSpeechToText");

        resetCounter();

        logPresentTargetFlow("end handleToggleCountdown");
    };

    const restartSTT = async (forWord: string, explicitContext?: string[]): Promise<void> => {
        logPresentTargetFlow("start restartSTT", { forWord, explicitContext });

        resetCounter();

        logPresentTargetFlow("trace restartSTT startSpeechToText");

        await startSpeechToText(forWord, explicitContext);

        logPresentTargetFlow("trace restartSTT startSpeechToText");

        setHasSucceded(false);

        logPresentTargetFlow("end restartSTT");
    };

    const handleOnTimerEnd = async (): Promise<void> => {
        logPresentTargetFlow("start handleOnTimerEnd");

        logPresentTargetFlow("trace handleOnTimerEnd stopSpeechToText");

        await stopSpeechToText();

        logPresentTargetFlow("trace handleOnTimerEnd stopSpeechToText");

        logPresentTargetFlow("trace handleOnTimerEnd handleUploadResult");

        void handleUploadResult(hasSucceeded, resultWord);

        logPresentTargetFlow("trace handleOnTimerEnd handleUploadResult");

        if ((!hasSucceeded && !isSecondTry) || !resultWord) {
            void handleOnWordError();
        } else {
            void goToNextWord();
        }

        logPresentTargetFlow("end handleOnTimerEnd");
    };

    const handleOnWordError = async (): Promise<void> => {
        logPresentTargetFlow("start handleOnWordError");

        if (isSecondTry) {
            void goToNextWord();
            return;
        }

        if (!resultWord) {
            setAlternates(alternates.concat(currentWord));
        }

        const titleForWord = translate("assessments.theWordIs", { word: currentWord.value });
        setIsSecondTry(true);
        setPromptTitle(titleForWord);

        if (currentAudio) {
            await playAudioAsync(currentAudio);
        }

        await restartSTT(currentWord.value);

        logPresentTargetFlow("end handleOnWordError");
    };

    const goToNextWord = async (): Promise<void> => {
        logPresentTargetFlow("start goToNextWord");

        const next = getNextElement(currentWord, assessment.wordLists[currentFlow.list].words);

        logPresentTargetFlow("trace goToNextWord", next);

        if (!next) {
            logPresentTargetFlow("trace goToNextWord finishPresentTargetsRun");

            await finishPresentTargetsRun();

            logPresentTargetFlow("trace goToNextWord finishPresentTargetsRun");
            return;
        }

        setResultWord(undefined);
        await restartSTT(next.value, [next.value, ...next.alternates]);
        setIsSecondTry(false);
        setPromptTitle("");
        loadAudioForWord(next);
        setCurrentWord(next);

        logPresentTargetFlow("end goToNextWord");
    };

    const { results, startSpeechToText, recordingVolume, stopSpeechToText, destroy, isRecording } =
        useCustomSpeechToText({
            context: [currentWord.value, ...currentWord.alternates]
        });

    const [counter, resetCounter, cancelCounter] = useCountDown({
        from: wordTime,
        onDone: async () => {
            void handleOnTimerEnd();
        }
    });

    const handleUploadResult = async (
        success: boolean,
        result?: ResultType,
        useTryNumber: boolean = true
    ): Promise<void> => {
        logPresentTargetFlow("start handleUploadResult");

        try {
            logPresentTargetFlow("trace handleUploadResult uploadAssessmentResponse");

            void uploadAssessmentResponse(assessment.id ?? 0, {
                stepName: currentFlow.name,
                targetWord: currentWord.value,
                response: result?.transcript === "-" ? undefined : result?.transcript,
                responseType: success ? WordResponseType.Correct : WordResponseType.Incorrect,
                audio: result?.speechBlob,
                tryNumber: useTryNumber ? (isSecondTry ? 2 : 1) : 0,
                source: result?.source
            });
        } catch (er) {
            logPresentTargetFlow("error handleUploadResult uploadAssessmentResponse", er);

            console.error(er);
        }

        logPresentTargetFlow("end handleUploadResult");
    };

    const handleResultForWord = async (incomingResults: ResultType[]): Promise<void> => {
        logPresentTargetFlow("start handleResultForWord");

        // check first google result
        const googleResults = incomingResults.filter((x) => x.source === SttSourceType.Google);

        const highestConfidenceResult = googleResults.sort((x, y) => y.confidence - x.confidence)[0];

        logPresentTargetFlow("trace handleResultForWord", highestConfidenceResult);

        // Prevent previous values from appearing, we don't care for late answers
        if (!highestConfidenceResult || highestConfidenceResult.intendedWord !== currentWord.value) {
            return;
        }

        let transcript = highestConfidenceResult?.transcript?.toLocaleLowerCase().split(" ");

        let success = false;
        let successResult: ResultType = highestConfidenceResult;

        for (const result of googleResults) {
            transcript = sanitizeResponse(result.transcript).split(" ");
            const intersectTranscript = intersection(acceptedWords, transcript);

            if (intersectTranscript.length > 0) {
                success = true;
                successResult = result;
                successResult.transcript = intersectTranscript[0];
                break;
            }
        }

        // check azure result
        const azureResults = incomingResults.find((x) => x.source === SttSourceType.Azure);

        const azureTranscript = sanitizeResponse(azureResults!.transcript.toLocaleLowerCase()).split(" ");
        const azureIntersectTranscript = intersection(acceptedWords, azureTranscript);

        // if google is success
        if (success) {
            // check if azure has match if not save alternate
            if (azureIntersectTranscript.length === 0 && azureResults?.transcript) {
                void handleUploadResult(false, azureResults, false);
            }
        } else {
            // set azure response if success
            if (azureIntersectTranscript.length > 0) {
                // save google response if contains alternate
                if (successResult?.transcript && successResult?.transcript !== "-") {
                    void handleUploadResult(false, successResult, false);
                }

                success = true;
                successResult = azureResults!;
                successResult.transcript = azureIntersectTranscript[0];
            } else {
                if (azureResults?.transcript) {
                    void handleUploadResult(false, azureResults, false);
                }
            }
        }

        setResultWord(successResult);

        if (success) {
            setHasSucceded(true);

            logPresentTargetFlow("start handleResultForWord stopSpeechToText");

            await stopSpeechToText();

            logPresentTargetFlow("start handleResultForWord stopSpeechToText");
        } else {
            setHasSucceded(false);
        }

        logPresentTargetFlow("end handleResultForWord");
    };

    const finishPresentTargetsRun = async (): Promise<void> => {
        logPresentTargetFlow("start finishPresentTargetsRun");

        setFinished(true);
        cancelCounter();
        setPromptTitle("");

        destroy();
        if (!assessmentFlow.flows[assessmentFlow.currentFlow + 1]) {
            navigate(NavItems.Assessments.AssessmentClosingVideo.path);
        }

        navigate(assessmentFlow.flows[assessmentFlow.currentFlow + 1].path);

        logPresentTargetFlow("end finishPresentTargetsRun");
    };

    useEffect(() => {
        logPresentTargetFlow("start useEffect currentWord");

        setAcceptedWords(getAcceptedWords(currentWord));

        logPresentTargetFlow("end useEffect currentWord");
    }, [currentWord]);

    useEffect(() => {
        logPresentTargetFlow("start useEffect results", results?.length);

        if (!results?.length) {
            return;
        }

        void handleResultForWord(results);

        logPresentTargetFlow("end useEffect results");
    }, [results]);

    useEffect(() => {
        logPresentTargetFlow("start useEffect");

        loadAudioForWord(currentWord);
        dispatch(updateCurrentFlow());

        logPresentTargetFlow("end useEffect");
    }, []);

    const progress = Math.floor(((wordTime - counter) * 100) / 6);
    const time = new Date((wordTime - (wordTime - counter)) * 1000).toISOString().slice(14, 19);

    const logPresentTargetFlow = (text: string, context: any = {}): void => {
        logData.part = logData.part + 1;
        logData.flow = currentFlow.name;
        logData.word = currentWord.value;
        logData.text = text;
        logData.context = JSON.stringify(context);

        appInsights.trackTrace({
            message: JSON.stringify(logData),
            severityLevel: SeverityLevel.Information
        });
    };

    return (
        <AssessmentShell micVolume={recordingVolume} micIsRecording={isRecording} navBarTitle={promptTitle}>
            {showCountdown ? (
                <>
                    <div style={{ paddingBottom: "6rem" }}>
                        <LCountDown from={3} onDone={handleToggleCountdown} />
                    </div>
                </>
            ) : (
                <div className="is-flex is-flex-direction-column" style={{ paddingBottom: "6rem" }}>
                    {showTimer && (
                        <div style={{ position: "absolute", right: "8%", top: "2%" }}>
                            <LProgressCircle
                                progress={progress}
                                text={time}
                                labelColor={Colors.primaryBlue}
                                trackColor={Colors.primaryBlue}
                                indicatorColor={Colors.primaryBlue}
                            />
                            <div>{results[0]?.transcript}</div>
                        </div>
                    )}
                    {!finished ? (
                        <LTitle overrideSize="5rem" color="has-text-dark-blue" className="mb-6">
                            {currentWord.value}
                        </LTitle>
                    ) : (
                        <FontAwesomeIcon icon={solid("circle-check")} size="6x" color={Colors.successGreen} />
                    )}
                </div>
            )}
            <AssessmentTimeout micVolume={recordingVolume} />
        </AssessmentShell>
    );
};
